import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { storage, db } from '../../lib/firebase';

// キャッシュ用のMap
const iconPathCache = new Map<string, string | null>();
const iconURLCache = new Map<string, { url: string; timestamp: number }>();

// キャッシュの有効期限（5分）
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * ワークスペースアイコンURLを取得する
 * @param workspaceId ワークスペースID
 * @returns アイコンURL（存在しない場合はnull）
 */
export const getWorkspaceIconURL = async (workspaceId: string): Promise<string | null> => {
  try {
    // キャッシュをチェック
    const cached = iconURLCache.get(workspaceId);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      return cached.url;
    }

    // FirestoreからiconPathを取得
    const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));

    if (workspaceDoc.exists()) {
      const workspaceData = workspaceDoc.data();

      // iconPathからURLを生成
      const iconPath = workspaceData.iconPath;
      if (iconPath) {
        // Firebase StorageのパスからURLを生成
        const storageRef = ref(storage, iconPath);
        const downloadURL = await getDownloadURL(storageRef);

        // キャッシュに保存
        iconURLCache.set(workspaceId, { url: downloadURL, timestamp: Date.now() });
        return downloadURL;
      }
    }

    return null;
  } catch (error) {
    console.error('❌ getWorkspaceIconURL: エラーが発生しました:', {
      workspaceId,
      error,
      timestamp: new Date().toISOString()
    });
    return null;
  }
};

/**
 * ワークスペースアイコンパスを取得する
 * @param workspaceId ワークスペースID
 * @returns アイコンパス（存在しない場合はnull）
 */
export const getWorkspaceIconPath = async (workspaceId: string): Promise<string | null> => {
  try {
    // キャッシュをチェック
    const cached = iconPathCache.get(workspaceId);
    if (cached !== undefined) {
      return cached;
    }

    const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));
    if (workspaceDoc.exists()) {
      const workspaceData = workspaceDoc.data();
      const iconPath = workspaceData.iconPath || null;
      
      // キャッシュに保存
      iconPathCache.set(workspaceId, iconPath);
      return iconPath;
    }
    
    // キャッシュに保存（null）
    iconPathCache.set(workspaceId, null);
    return null;
  } catch (error) {
    console.error('❌ getWorkspaceIconPath: エラーが発生しました:', error);
    return null;
  }
};

/**
 * ワークスペースアイコンのキャッシュをクリアする
 * @param workspaceId ワークスペースID（指定しない場合は全キャッシュをクリア）
 */
export const clearWorkspaceIconCache = (workspaceId?: string) => {
  if (workspaceId) {
    iconPathCache.delete(workspaceId);
    iconURLCache.delete(workspaceId);
  } else {
    iconPathCache.clear();
    iconURLCache.clear();
  }
};
